/**
 * Consultants List Page
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Skeleton,
  IconButton,
  Menu,
  MenuItem,
  TablePagination,
  Avatar,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { PageHeader, ConfirmDialog } from '../../components/common';
import {
  useGetConsultantsQuery,
  useDeleteConsultantMutation,
  useActivateConsultantMutation,
  useDeactivateConsultantMutation,
} from '../../store/api/consultantsApi';
import { Consultant } from '../../types/user';
import moment from 'moment';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../hooks/usePermissions';
import ProviderListFilters from '../../components/providers/ProviderListFilters';
import type { ProviderListFilters as ProviderFilters } from '../../types/providerProfile';

const ConsultantsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canManageConsultants = hasPermission('manage_consultants');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ProviderFilters>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConsultant, setSelectedConsultant] = useState<Consultant | null>(null);
  const [deleteDialog, setDeleteDialog] = useState(false);

  const [deleteConsultant, { isLoading: isDeleting }] = useDeleteConsultantMutation();
  const [activateConsultant] = useActivateConsultantMutation();
  const [deactivateConsultant] = useDeactivateConsultantMutation();

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Consultants query
  const { data: consultantsData, isLoading: consultantsLoading } = useGetConsultantsQuery({
    page: page + 1,
    page_size: rowsPerPage,
    search: search || undefined,
    ...filters,
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, consultant: Consultant) => {
    setAnchorEl(event.currentTarget);
    setSelectedConsultant(consultant);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConsultant(null);
  };

  const handleDelete = async () => {
    if (!selectedConsultant) return;

    try {
      await deleteConsultant(selectedConsultant.id).unwrap();
      toast.success(t('consultants.toast.deleteSuccess'));
      setDeleteDialog(false);
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.deleteError'));
    }
  };

  const handleActivate = async (consultant: Consultant) => {
    try {
      await activateConsultant(consultant.id).unwrap();
      toast.success(t('consultants.toast.activateSuccess'));
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.activateError'));
    }
  };

  const handleDeactivate = async (consultant: Consultant) => {
    try {
      await deactivateConsultant(consultant.id).unwrap();
      toast.success(t('consultants.toast.deactivateSuccess'));
      handleMenuClose();
    } catch (err: any) {
      toast.error(err?.data?.message || t('consultants.toast.deactivateError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'REJECTED':
        return 'error';
      default:
        return 'default';
    }
  };

  const consultantsColumns = [
    { id: 'id', label: t('consultants.cols.id'), minWidth: 80 },
    { id: 'profile', label: t('consultants.cols.profile'), minWidth: 150 },
    { id: 'full_name', label: t('consultants.cols.full_name'), minWidth: 150 },
    { id: 'email', label: t('consultants.cols.email'), minWidth: 150 },
    { id: 'phone_number', label: t('consultants.cols.phone_number'), minWidth: 120 },
    { id: 'age', label: t('consultants.cols.age'), minWidth: 80 },
    { id: 'specialization', label: t('consultants.cols.specialization'), minWidth: 150 },
    { id: 'service_areas', label: t('providers.cols.areas'), minWidth: 160 },
    { id: 'status', label: t('consultants.cols.status'), minWidth: 100 },
    { id: 'is_active', label: t('consultants.cols.is_active'), minWidth: 80 },
    { id: 'rating', label: t('consultants.cols.rating'), minWidth: 100 },
    { id: 'total_consultations', label: t('consultants.cols.total_consultations'), minWidth: 120 },
    { id: 'created_at', label: t('consultants.cols.created_at'), minWidth: 120 },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={t('consultants.title')}
        breadcrumbs={[
          { label: t('nav.dashboard'), path: '/' },
          { label: t('nav.consultants') },
        ]}
        {...(canManageConsultants
          ? {
              actionLabel: t('consultants.buttons.add'),
              actionIcon: <AddIcon />,
              onAction: () => navigate('/consultants/create'),
            }
          : {})}
      />

      {/* Search Filter */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField
            placeholder={t('consultants.filters.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchInput && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchInput('');
                      setSearch('');
                    }}
                    edge="end"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <ProviderListFilters
            value={filters}
            onChange={(next) => {
              setFilters(next);
              setPage(0);
            }}
          />
        </CardContent>
      </Card>

      {/* Consultants Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {consultantsColumns.map((column) => (
                  <TableCell key={column.id} sx={{ fontWeight: 600 }}>
                    {column.label}
                  </TableCell>
                ))}
                {canManageConsultants && (
                  <TableCell sx={{ fontWeight: 600, width: 50 }}>{t('common.actions')}</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {consultantsLoading ? (
                Array.from({ length: 5 }).map((_, index) => (
                  <TableRow key={index}>
                    {consultantsColumns.map((column) => (
                      <TableCell key={column.id}>
                        <Skeleton variant="text" width="100%" />
                      </TableCell>
                    ))}
                    {canManageConsultants && (
                      <TableCell>
                        <Skeleton variant="circular" width={40} height={40} />
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : consultantsData?.results && consultantsData.results.length > 0 ? (
                consultantsData.results.map((consultant: Consultant) => (
                  <TableRow
                    key={consultant.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/consultants/${consultant.id}`)}
                  >
                    <TableCell>{consultant.id}</TableCell>
                    <TableCell>
                      <Avatar
                        src={consultant.profile_picture || undefined}
                        alt={consultant.full_name}
                        sx={{ width: 40, height: 40 }}
                      >
                        {consultant.full_name.charAt(0)}
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ fontWeight: 500 }}>{consultant.full_name}</Box>
                        {consultant.is_verified && (
                          <Chip
                            label={t('consultants.verified')}
                            size="small"
                            color="success"
                            sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{consultant.email || '—'}</TableCell>
                    <TableCell>{consultant.phone_number}</TableCell>
                    <TableCell>{consultant.age || t('consultants.na')}</TableCell>
                    <TableCell>{consultant.specialization || '—'}</TableCell>
                    <TableCell>
                      {[...(consultant.service_cities ?? []), ...(consultant.service_areas ?? [])].join('، ') || '—'}
                    </TableCell>
                    <TableCell>
                      {consultant.status ? (
                        <Chip
                          label={t(`consultants.status.${consultant.status}`)}
                          size="small"
                          color={getStatusColor(consultant.status) as any}
                        />
                      ) : (
                        <Chip label={t('consultants.na')} size="small" color="default" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={consultant.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                        label={consultant.is_active ? t('consultants.status.active') : t('consultants.status.inactive')}
                        size="small"
                        color={consultant.is_active ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <span>⭐</span>
                        <span>{parseFloat(consultant.rating).toFixed(1)}</span>
                        <span style={{ fontSize: '0.75rem', color: '#666' }}>
                          ({consultant.total_reviews})
                        </span>
                      </Box>
                    </TableCell>
                    <TableCell>{consultant.total_consultations}</TableCell>
                    <TableCell>
                      {moment(consultant.created_at).format('MMM DD, YYYY')}
                    </TableCell>
                    {canManageConsultants && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, consultant)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={consultantsColumns.length + (canManageConsultants ? 1 : 0)}
                    align="center"
                    sx={{ py: 5 }}
                  >
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('consultants.empty')}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {consultantsData && (
          <TablePagination
            component="div"
            count={consultantsData.count || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Card>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {selectedConsultant && (
          <>
            <MenuItem
              onClick={() => {
                navigate(`/consultants/${selectedConsultant.id}`);
                handleMenuClose();
              }}
            >
              <ViewIcon sx={{ mr: 1 }} fontSize="small" />
              {t('consultants.buttons.viewDetails')}
            </MenuItem>
            {canManageConsultants && (
              <>
                <MenuItem
                  onClick={() => {
                    navigate(`/consultants/${selectedConsultant.id}/edit`);
                    handleMenuClose();
                  }}
                >
                  <EditIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('common.edit')}
                </MenuItem>
                {selectedConsultant.is_active ? (
                  <MenuItem
                    onClick={() => {
                      handleDeactivate(selectedConsultant);
                    }}
                  >
                    <CancelIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('consultants.buttons.deactivate')}
                  </MenuItem>
                ) : (
                  <MenuItem
                    onClick={() => {
                      handleActivate(selectedConsultant);
                    }}
                  >
                    <CheckCircleIcon sx={{ mr: 1 }} fontSize="small" />
                    {t('consultants.buttons.activate')}
                  </MenuItem>
                )}
                <MenuItem
                  onClick={() => {
                    setDeleteDialog(true);
                    handleMenuClose();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
                  {t('common.delete')}
                </MenuItem>
              </>
            )}
          </>
        )}
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        onConfirm={handleDelete}
        title={t('consultants.dialog.deleteTitle')}
        message={t('consultants.dialog.deleteMessage', { name: selectedConsultant?.full_name })}
        confirmText={t('common.delete')}
        cancelText={t('common.cancel')}
        loading={isDeleting}
      />
    </Box>
  );
};

export default ConsultantsListPage;

